/**
 * Plain functions of the sold services' pages (ADR-026 §5, §6): the states and their tones, where a
 * service stands against its due date, and which staff operations it takes. Kept free of React so
 * `tests/subscriptions.test.mjs` runs them in Node, against the server's own vocabularies.
 */
import type { CalendarStep, PeriodKind, Subscription, SubscriptionState, SuspendReason } from '../api/areas/subscriptions';

/** Every state of `subscription.subscriptions` (commerce_subscriptions::State). */
export const SUBSCRIPTION_STATES: readonly SubscriptionState[] = [
  'PENDING',
  'PROVISIONING',
  'ACTIVE',
  'OVERDUE',
  'SUSPENDED',
  'CANCELLATION_PENDING',
  'TERMINATED',
  'CANCELLED',
  'FRAUD',
];

/** The steps of the overdue calendar (`subscription.calendar_events.event`). */
export const CALENDAR_STEPS: readonly CalendarStep[] = ['RENEWAL_ORDER', 'REMINDER', 'OVERDUE', 'UNPAID_SIGNAL', 'LATE_FEE', 'SUSPENDED', 'TERMINATED', 'TERMINATION_NOTICE'];

/** The kinds of a paid period (`subscription.periods.kind`). */
export const PERIOD_KINDS: readonly PeriodKind[] = ['FIRST', 'RENEWAL', 'CHANGE', 'EXTENSION', 'TRIAL'];

/** Why a service is suspended (the calendar's `NON_PAYMENT`, or staff's three). */
export const SUSPEND_REASONS: readonly SuspendReason[] = ['NON_PAYMENT', 'ABUSE', 'CUSTOMER_REQUEST', 'OTHER'];

/** What staff may give as the reason of a suspension by hand (commerce_subscriptions::staff::SUSPEND_REASONS). */
export const STAFF_SUSPEND_REASONS = ['ABUSE', 'CUSTOMER_REQUEST', 'OTHER'] as const;

export type Tone = 'neutral' | 'info' | 'ok' | 'pending' | 'warn' | 'serious' | 'crit';

export const STATE_TONE: Record<SubscriptionState, Tone> = {
  PENDING: 'pending',
  PROVISIONING: 'pending',
  ACTIVE: 'ok',
  OVERDUE: 'warn',
  SUSPENDED: 'serious',
  CANCELLATION_PENDING: 'info',
  TERMINATED: 'neutral',
  CANCELLED: 'neutral',
  FRAUD: 'crit',
};

/** Extensions above this many days need `subscription.extend.large` (LARGE_EXTENSION_DAYS). */
export const LARGE_EXTENSION_DAYS = 31;

/** Whole days from `today` to `day` (both `YYYY-MM-DD`): negative once past. */
export function daysUntil(day: string, today: string): number {
  const ms = Date.parse(`${day}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`);
  return Math.round(ms / 86_400_000);
}

/** Today as a local day in Vietnam (UTC+7, no daylight saving), as the calendar counts days. */
export function vietnamToday(now: Date = new Date()): string {
  return new Date(now.getTime() + 7 * 3_600_000).toISOString().slice(0, 10);
}

/** Where a service stands against its due date: the key of `subscriptions:due.*` and its count. */
export function dueStanding(s: Pick<Subscription, 'state' | 'next_due_on'>, today: string): { key: 'none' | 'ended' | 'later' | 'soon' | 'today' | 'overdue'; days: number } {
  if (s.state === 'TERMINATED' || s.state === 'CANCELLED') return { key: 'ended', days: 0 };
  if (!s.next_due_on) return { key: 'none', days: 0 };
  const d = daysUntil(s.next_due_on, today);
  if (d < 0) return { key: 'overdue', days: -d };
  if (d === 0) return { key: 'today', days: 0 };
  return { key: d <= 14 ? 'soon' : 'later', days: d };
}

export type StaffOperation = 'extend' | 'suspend' | 'unsuspend' | 'terminate';

/**
 * The operations a service takes in its state, as commerce_subscriptions::staff allows them, and
 * the permission each needs (the page shows a button only to who holds it).
 */
export function operationsFor(s: Pick<Subscription, 'state' | 'next_due_on'>): { op: StaffOperation; permission: string }[] {
  const out: { op: StaffOperation; permission: string }[] = [];
  const live = s.state === 'ACTIVE' || s.state === 'OVERDUE' || s.state === 'SUSPENDED';
  if (live && s.next_due_on) out.push({ op: 'extend', permission: 'subscription.operate' });
  if (s.state === 'ACTIVE' || s.state === 'OVERDUE') out.push({ op: 'suspend', permission: 'subscription.operate' });
  if (s.state === 'SUSPENDED') out.push({ op: 'unsuspend', permission: 'subscription.operate' });
  if (live || s.state === 'CANCELLATION_PENDING') out.push({ op: 'terminate', permission: 'subscription.terminate' });
  return out;
}

/** The permissions an extension of `days` needs. */
export function extensionPermissions(days: number): string[] {
  return days > LARGE_EXTENSION_DAYS ? ['subscription.operate', 'subscription.extend.large'] : ['subscription.operate'];
}

/** `758400.000000` → `758.400` (whole đồng, Vietnamese grouping). */
export function vnd(amount: string | null | undefined): string {
  if (amount == null || amount === '') return '—';
  const n = Math.round(Number(amount));
  if (!Number.isFinite(n)) return amount;
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}
